import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Eye, X, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useEmployees, useHrContext } from '@/hooks/use-api'
import { useHrPreview, setHrPreview, clearHrPreview } from '@/lib/hrPreview'

/**
 * HR "preview as" control, shown across the HR workspace. HR admins can preview
 * the HR system as any employee (read-only); a banner shows the active preview
 * with an exit. Switching invalidates HR queries so the whole UI re-scopes.
 */
export function HrPreviewBar() {
  const location = useLocation()
  const queryClient = useQueryClient()
  const { data: ctx } = useHrContext()
  const preview = useHrPreview()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [search, setSearch] = useState('')

  const isHrArea = location.pathname.startsWith('/hr')
  // Search the directory only while the picker is open (HR admins see everyone).
  const { data } = useEmployees({ search: search.trim() || undefined, limit: 25 })
  const results = useMemo(() => data?.data ?? [], [data])

  if (!isHrArea) return null

  const refreshHr = () => queryClient.invalidateQueries({ queryKey: ['hr'] })

  const startPreview = (id: string, name: string) => {
    setHrPreview(id, name)
    setPickerOpen(false)
    setSearch('')
    refreshHr()
  }
  const exitPreview = () => {
    clearHrPreview()
    refreshHr()
  }

  // Active preview → banner. (When previewing, ctx.isHrAdmin reflects the target,
  // so we rely on the persisted preview state, not the role, to show this.)
  if (preview.employeeId) {
    return (
      <div
        role="status"
        className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-[#941052]/40 bg-[#941052]/10 px-3 py-2 text-sm"
      >
        <span className="flex items-center gap-2 text-[#072744]">
          <Eye className="h-4 w-4" aria-hidden="true" />
          Previewing HR as <strong>{preview.employeeName || 'employee'}</strong> — read-only.
        </span>
        <Button size="sm" variant="outline" onClick={exitPreview}>
          <X className="mr-1 h-4 w-4" aria-hidden="true" />
          Exit preview
        </Button>
      </div>
    )
  }

  // Not previewing: HR admins get a "Preview as…" entry point.
  if (!ctx?.isHrAdmin) return null
  return (
    <>
      <div className="mb-4 flex justify-end">
        <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)}>
          <Eye className="mr-1 h-4 w-4" aria-hidden="true" />
          Preview as…
        </Button>
      </div>

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Preview the HR system as…</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search employees by name…"
              aria-label="Search employees to preview as"
              className="pl-8"
            />
          </div>
          <ul className="max-h-72 divide-y divide-border overflow-auto rounded-md border border-border">
            {results.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">No matches.</li>
            ) : (
              results.map((e) => {
                const name = `${e.preferred_name || e.first_name} ${e.last_name}`.trim()
                return (
                  <li key={e.id}>
                    <button
                      type="button"
                      onClick={() => startPreview(e.id, name)}
                      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted focus:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                    >
                      <span className="truncate">{name}</span>
                      {e.job_title && (
                        <span className="ml-2 truncate text-xs text-muted-foreground">
                          {e.job_title}
                        </span>
                      )}
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </DialogContent>
      </Dialog>
    </>
  )
}
